import { getScanPhotoImageUrl, getTaskModifierDisplayNames } from '@kaiizen/kai-pro-api';
import type {
  ScopeReportLineItem,
  ScopeReportLineItemWithChangeData,
  TaskModifierDisplayNames,
} from '@kaiizen/kai-pro-api';
import {
  findLineItemKey,
  formatCurrency,
  formatWorkStatus,
  getChangeTypeLabel,
  getGroupName,
  getTaskName,
} from '@kaiizen/kai-reports';
import {
  getScanLineItem,
  getScanWithUpdatedLineItem,
  getUpdatedScanValue,
  type ScanRootOptimized,
  type ScanTemplateValue,
  type ScanValue,
} from '@kaiizen/kai-scan-template-system';

export type HistoricalArtifactTask = {
  amount: number;
  badges: string[];
  baselineAmount: number;
  baselineContractorName?: string;
  baselineQuantityLabel?: string;
  changeLabels: string[];
  contractorName: string;
  description?: string;
  id: string;
  items: ScopeReportLineItemWithChangeData[];
  noteLines: string[];
  photos: NonNullable<ScopeReportLineItem['taskPhotos']>;
  productLines: string[];
  quantityLabel: string;
  rateLabel: string;
  taskName: string;
  title: string;
};

export type HistoricalArtifactRoom = {
  id: string;
  name: string;
  noteLines: string[];
  tasks: HistoricalArtifactTask[];
  totalAmount: number;
};

export type HistoricalArtifactPhoto = {
  dataUrl: string;
  id: string;
  title: string;
};

export type HistoricalArtifactPhotoGalleryTask = {
  id: string;
  photos: HistoricalArtifactPhoto[];
  title: string;
};

export type HistoricalArtifactPhotoGalleryGroup = {
  id: string;
  tasks: HistoricalArtifactPhotoGalleryTask[];
  title: string;
};

type HistoricalArtifactPhotoGalleryTaskDetails = {
  title: string;
};

type HistoricalArtifactTaskAmountComponent = {
  scopeLineItem: ScopeReportLineItem;
  valueKey: 'laborCost' | 'materialCost';
  valueTemplate: ScanTemplateValue;
  scanValue: ScanValue | undefined;
  value: number;
};

const HISTORICAL_ARTIFACT_COST_VALUE_KEYS = [
  'materialCost',
  'laborCost',
] as const;

const uniqueStrings = (values: Array<string | null | undefined>) => [
  ...new Set(
    values
      .map(value => value?.trim())
      .filter((value): value is string => !!value),
  ),
];

const formatQuantity = (value: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);

const formatQuantityUnit = (quantity: number, unit: string) => {
  if (!unit || unit === 'count') return formatQuantity(quantity);
  if (unit === 'each') return `${formatQuantity(quantity)} ea`;
  return `${formatQuantity(quantity)} ${unit}`;
};

const getQuantityLabel = (items: ScopeReportLineItem[]) => {
  const quantities = items.filter(item => item.productQuantity != null);
  if (quantities.length === 0) return '--';

  const units = uniqueStrings(
    quantities.map(item => item.productQuantityValueType || 'count'),
  );
  if (units.length !== 1) return `${formatQuantity(quantities.length)} items`;

  return formatQuantityUnit(
    quantities.reduce((sum, item) => sum + (item.productQuantity ?? 0), 0),
    units[0],
  );
};

const getRateLabel = (items: ScopeReportLineItem[]) => {
  const rates = uniqueStrings(
    items.map(
      item => `${item.unitPrice}::${item.productQuantityValueType || 'count'}`,
    ),
  );
  if (rates.length !== 1) return 'Mixed';

  const item = items[0];
  if (!item) return '--';

  const unit = item.productQuantityValueType;
  const suffix = !unit || unit === 'count' ? '' : unit === 'each' ? '/ea' : `/${unit}`;
  return `${formatCurrency(item.unitPrice)}${suffix}`;
};

const getProductLines = (items: ScopeReportLineItem[], taskTitle: string) =>
  uniqueStrings(
    items.map(item => {
      const productParts = uniqueStrings([
        item.productName ?? undefined,
        item.productBrand ? `Brand: ${item.productBrand}` : undefined,
        item.productSku ? `SKU: ${item.productSku}` : undefined,
      ]);
      if (productParts.length > 0) return productParts.join(' · ');
      return item.optionName !== taskTitle
        ? `Option: ${item.optionName}`
        : undefined;
    }),
  );

const getPhotoGalleryTaskTitle = (scopeLineItem: ScopeReportLineItem) =>
  scopeLineItem.scopePDFTaskDescName || scopeLineItem.taskName || scopeLineItem.lineItemName || 'Scope item';

const getPhotoGalleryTaskDetailsById = (scopeLineItems: ScopeReportLineItem[]) =>
  new Map<string, HistoricalArtifactPhotoGalleryTaskDetails>(
    scopeLineItems.map(scopeLineItem => [
      scopeLineItem.taskId,
      { title: getPhotoGalleryTaskTitle(scopeLineItem) },
    ]),
  );

const getHistoricalArtifactPhoto = (photoId: string, title: string): HistoricalArtifactPhoto => ({
  dataUrl: getScanPhotoImageUrl(photoId, { maxDimension: 1024 }),
  id: photoId,
  title,
});

/** Builds the full-resolution photo groups for a filtered audience-document gallery. */
export const buildHistoricalArtifactPhotoGalleryGroups = (
  scanRoot: ScanRootOptimized | undefined,
  scopeLineItems: ScopeReportLineItem[],
  includeAllGroupPhotos = true,
): HistoricalArtifactPhotoGalleryGroup[] => {
  if (!scanRoot?.groups) return [];

  const photoIds = new Set<string>();
  const taskDetailsById = getPhotoGalleryTaskDetailsById(scopeLineItems);

  return scanRoot.groups.flatMap(group => {
    const eligibleTasks = group.tasks.filter(task => includeAllGroupPhotos || taskDetailsById.has(task.id));
    const taskPhotoIdsByTaskId = new Map(
      eligibleTasks.map(task => [task.id, new Set(task.optimized.allPhotos.map(photo => photo.photoId))]),
    );
    const photosByTaskId = new Map<string, HistoricalArtifactPhoto[]>();
    const groupPhotos: HistoricalArtifactPhoto[] = [];

    for (const photo of group.optimized.allPhotos) {
      if (photoIds.has(photo.photoId)) continue;

      const task = eligibleTasks.find(task => taskPhotoIdsByTaskId.get(task.id)?.has(photo.photoId));
      if (!task && !includeAllGroupPhotos) continue;
      photoIds.add(photo.photoId);

      if (task) {
        const taskDetails = taskDetailsById.get(task.id);
        const photos = photosByTaskId.get(task.id) ?? [];
        photos.push(getHistoricalArtifactPhoto(photo.photoId, taskDetails?.title ?? getTaskName(task)));
        photosByTaskId.set(task.id, photos);
      } else if (includeAllGroupPhotos) {
        groupPhotos.push(getHistoricalArtifactPhoto(photo.photoId, 'Group photo'));
      }
    }

    const tasks = eligibleTasks.flatMap(task => {
      const photos = photosByTaskId.get(task.id) ?? [];
      return photos.length > 0
        ? [{ id: task.id, photos, title: taskDetailsById.get(task.id)?.title ?? getTaskName(task) }]
        : [];
    });
    if (groupPhotos.length > 0) {
      tasks.push({ id: `${group.id}-photos`, photos: groupPhotos, title: 'Group photos' });
    }

    return tasks.length > 0
      ? [{
        id: group.id,
        tasks,
        title: scopeLineItems.find(scopeLineItem => scopeLineItem.groupId === group.id)?.groupName ?? getGroupName(group),
      }]
      : [];
  });
};

const getLineItemRow = (
  item: ScopeReportLineItemWithChangeData,
  modifierLabels: TaskModifierDisplayNames,
): HistoricalArtifactTask => {
  const title = item.lineItemName
    || item.scopePDFTaskDescName
    || item.taskName
    || 'Scope item';
  const changeLabel = item.changeOrderData ? getChangeTypeLabel(item.changeOrderData, modifierLabels) : undefined;
  const baselineQuantity = item.changeOrderData?.baselineQuantity;
  const baselineQuantityLabel = item.changeOrderData?.changedColumns.quantity
      && baselineQuantity != null
    ? formatQuantityUnit(baselineQuantity, item.productQuantityValueType || 'count')
    : undefined;
  const baselineContractorName = item.changeOrderData?.changedColumns.contractor
      && item.changeOrderData.baselineContractorName
    ? item.changeOrderData.baselineContractorName
    : undefined;

  return {
    amount: item.totalLineItemCost,
    baselineAmount: item.changeOrderData?.baselineTotalPrice ?? item.totalLineItemCost,
    baselineContractorName,
    baselineQuantityLabel,
    changeLabels: changeLabel ? [changeLabel] : [],
    badges: uniqueStrings([
      item.isDeferredLineItem ? modifierLabels.deferred : undefined,
      item.isTenantRespLineItem ? modifierLabels.tenantResponsibility : undefined,
      item.isEditRequest && !item.isEditResolution ? 'Needs edit' : undefined,
      item.isStatusTask ? `Status: ${item.statusLabel || item.statusValue || '-'}` : undefined,
      item.taskWorkStatus ? `Work status: ${formatWorkStatus(item.taskWorkStatus)}` : undefined,
    ]),
    contractorName: item.contractorName,
    description: item.isOtherTask ? item.scopePDFTaskDescName ?? '' : '',
    id: item.lineItemId,
    items: [item],
    noteLines: uniqueStrings([
      ...(item.taskNotes ?? []).map(note => `Task note: ${note}`),
      ...(item.lineItemNotes ?? []).map(note => `Line item note: ${note}`),
    ]),
    photos: item.taskPhotos ?? [],
    productLines: getProductLines([item], title),
    quantityLabel: getQuantityLabel([item]),
    rateLabel: getRateLabel([item]),
    taskName: item.taskName,
    title,
  };
};

const getHistoricalArtifactTaskAmountComponents = (
  scan: ScanRootOptimized,
  task: HistoricalArtifactTask,
): HistoricalArtifactTaskAmountComponent[] =>
  task.items.flatMap(scopeLineItem => {
    const lineItem = getScanLineItem(
      scan,
      scopeLineItem.groupId,
      scopeLineItem.taskId,
      scopeLineItem.optionId,
      scopeLineItem.lineItemId,
    );

    return HISTORICAL_ARTIFACT_COST_VALUE_KEYS.flatMap(valueKey => {
      const valueTemplate = lineItem.template.values?.[valueKey];
      const value = lineItem.optimized.reducedValues[valueKey];
      return valueTemplate && value != null
        ? [{ scopeLineItem, valueKey, valueTemplate, scanValue: lineItem.values?.[valueKey], value }]
        : [];
    });
  });

const getScanWithUpdatedHistoricalArtifactTaskAmountComponent = (
  scan: ScanRootOptimized,
  component: HistoricalArtifactTaskAmountComponent,
  value: number,
) =>
  getScanWithUpdatedLineItem(
    scan,
    component.scopeLineItem.groupId,
    component.scopeLineItem.taskId,
    component.scopeLineItem.optionId,
    component.scopeLineItem.lineItemId,
    { values: { [component.valueKey]: getUpdatedScanValue(component.valueTemplate, component.scanValue, value) } },
  );

/** Returns baseline line-item totals from the existing scan-diff report data. */
export const getHistoricalArtifactTaskBaselineAmounts = (
  scopeLineItems: ScopeReportLineItemWithChangeData[],
) =>
  scopeLineItems.reduce((lineItemAmounts, scopeLineItem) => {
    lineItemAmounts.set(
      scopeLineItem.lineItemId,
      scopeLineItem.changeOrderData?.baselineTotalPrice ?? scopeLineItem.totalLineItemCost,
    );
    return lineItemAmounts;
  }, new Map<string, number>());

/**
 * Applies a task-total edit to the cloned scan's line-item cost values.
 *
 * A reduction drains cost components from the final line item backward, so no
 * component becomes negative; an increase lands on the final component. The
 * entire update is one pure scan-root transformation for `scanDataMutators`.
 */
export const getScanWithUpdatedHistoricalArtifactTaskAmount = (
  scan: ScanRootOptimized,
  task: HistoricalArtifactTask,
  updatedAmount: number,
) => {
  const components = getHistoricalArtifactTaskAmountComponents(scan, task);
  const currentAmount = components.reduce((amount, component) => amount + component.value, 0);
  const amountDifference = updatedAmount - currentAmount;
  if (amountDifference === 0 || components.length === 0) return scan;

  if (amountDifference > 0) {
    const component = components.at(-1)!;
    return getScanWithUpdatedHistoricalArtifactTaskAmountComponent(
      scan,
      component,
      component.value + amountDifference,
    );
  }

  let remainingReduction = -amountDifference;
  let updatedScan = scan;
  for (const component of [...components].reverse()) {
    if (remainingReduction === 0) break;
    const nextValue = Math.max(0, component.value - remainingReduction);
    remainingReduction -= component.value - nextValue;
    if (nextValue !== component.value) {
      updatedScan = getScanWithUpdatedHistoricalArtifactTaskAmountComponent(
        updatedScan,
        component,
        nextValue,
      );
    }
  }
  return updatedScan;
};

/** Groups report line items into the room/line-item document structure used by historical artifacts. */
export const buildHistoricalArtifactRooms = (
  items: ScopeReportLineItemWithChangeData[],
  modifierLabels: TaskModifierDisplayNames = getTaskModifierDisplayNames(undefined),
): HistoricalArtifactRoom[] => {
  const itemsByLineItemKey = new Map<string, ScopeReportLineItemWithChangeData>();
  const roomOrder: string[] = [];
  const lineItemOrderByRoom = new Map<string, string[]>();

  for (const item of items) {
    const lineItemKey = findLineItemKey(item);
    if (itemsByLineItemKey.has(lineItemKey)) continue;

    if (!lineItemOrderByRoom.has(item.groupId)) {
      roomOrder.push(item.groupId);
      lineItemOrderByRoom.set(item.groupId, []);
    }
    lineItemOrderByRoom.get(item.groupId)?.push(lineItemKey);
    itemsByLineItemKey.set(lineItemKey, item);
  }

  return roomOrder.flatMap(groupId => {
    const lineItemKeys = lineItemOrderByRoom.get(groupId) ?? [];
    const roomItems = lineItemKeys.flatMap(
      lineItemKey => itemsByLineItemKey.get(lineItemKey) ?? [],
    );
    const firstItem = roomItems[0];
    if (!firstItem) return [];

    const tasks = lineItemKeys
      .map(lineItemKey => itemsByLineItemKey.get(lineItemKey))
      .filter((item): item is ScopeReportLineItemWithChangeData => !!item)
      .map(item => getLineItemRow(item, modifierLabels));

    return [
      {
        id: groupId,
        name: firstItem.groupName,
        noteLines: uniqueStrings(
          roomItems.flatMap(item => item.groupNotes ?? []),
        ),
        tasks,
        totalAmount: tasks.reduce((sum, task) => sum + task.amount, 0),
      },
    ];
  });
};
